import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DeployResumo } from '@core/types';
import Layout from '../componentes/Layout';
import Carregando from '../componentes/Carregando';
import Aviso from '../componentes/Aviso';
import {
  acessosPorRota, dispararDeploy, errosRecentes, listarDeploys, metricasNegocio,
  saidaDoDeploy, saudeSistema, statusDaVps,
} from '../lib/consultas';
import {
  cobertura, formatarBytes, formatarDuracao, formatarIdade, nivelDaCpu, nivelDaMemoria,
  nivelDaTaxaDeErro, nivelDoBackup, nivelDoDisco, pior, progressoDoDeploy, resumirContainers,
  rotuloDaEtapa, taxaDeErro, textoDoBackup, type Nivel,
} from '../lib/sistema';
import { brl, dataHoraBR, qtd } from '../lib/formato';
import { mensagemDeErro } from '../lib/supabase';

// Aba Sistema (migração 019) — só o dono.
//
// A tela existe para responder, numa olhada, perguntas que antes exigiam SSH:
// "o backup rodou?", "o disco vai encher?", "o que está no ar é o código de
// hoje?", "algo está dando erro para o cliente?". Em 07/08/2026 o agente ficou
// 2331 reinícios sem ninguém perceber, e o backup passou dias sem existir —
// os dois casos que esta tela torna impossíveis de ignorar.
//
// O LIMIAR NÃO MORA AQUI: quem decide o que é "atenção" e "crítico" é
// lib/sistema.ts, que é função pura e testada. Esta tela pinta o que ele disser.
// O banco entrega o FATO; a régua é de produto e muda com o tempo.
//
// DUAS FONTES, DE PROPÓSITO: as RPCs leem o que o agente já gravou (histórico,
// agregados); o /status do agente mede a máquina AGORA. Quem abre esta tela
// costuma estar desconfiando de algo neste minuto.

const COR: Record<Nivel, string> = {
  ok: 'text-ok',
  atencao: 'text-alerta',
  critico: 'text-erro',
};
const BORDA: Record<Nivel, string> = {
  ok: 'border-edge',
  atencao: 'border-alerta/50',
  critico: 'border-erro/60',
};

export default function Sistema() {
  const cliente = useQueryClient();
  const agora = Date.now();

  // A saúde vem do banco (barata, agregada) e se atualiza sozinha; o /status ao
  // vivo é para o momento em que se desconfia — e ele é o único que sabe se um
  // deploy está rodando AGORA.
  const saude = useQuery({
    queryKey: ['sistema', 'saude'],
    queryFn: saudeSistema,
    refetchInterval: 60_000,
  });
  const metricas = useQuery({ queryKey: ['sistema', 'metricas'], queryFn: metricasNegocio });
  const deploys = useQuery({
    queryKey: ['sistema', 'deploys'],
    queryFn: () => listarDeploys(10),
    // Enquanto há deploy rodando, a lista é o progresso: 5s.
    refetchInterval: (q) =>
      (q.state.data ?? []).some((d) => d.status === 'rodando') ? 5_000 : false,
  });
  const rotas = useQuery({ queryKey: ['sistema', 'rotas'], queryFn: () => acessosPorRota({ dias: 7 }) });
  const erros = useQuery({ queryKey: ['sistema', 'erros'], queryFn: () => errosRecentes(20) });

  const publicar = useMutation({
    mutationFn: dispararDeploy,
    onSuccess: () => {
      cliente.invalidateQueries({ queryKey: ['sistema', 'deploys'] });
      cliente.invalidateQueries({ queryKey: ['sistema', 'saude'] });
    },
  });

  const s = saude.data;
  const containers = resumirContainers(s?.containers ?? null);
  const nDisco = nivelDoDisco(s?.disco_usado_gb ?? null, s?.disco_total_gb ?? null);
  const nMem = nivelDaMemoria(s?.mem_usada_mb ?? null, s?.mem_total_mb ?? null);
  const nCpu = nivelDaCpu(s?.cpu_pct ?? null);
  const nBackup = nivelDoBackup(s?.backup_em ?? null, agora);
  const nErros = nivelDaTaxaDeErro(s?.acessos_24h ?? 0, s?.erros_24h ?? 0);
  const geral = pior([nDisco, nMem, nCpu, nBackup, nErros, containers.nivel]);

  const rodando = (deploys.data ?? []).find((d) => d.status === 'rodando');

  return (
    <Layout titulo="Sistema">
      <div className="space-y-4">
        {saude.error ? <Aviso tipo="erro">{mensagemDeErro(saude.error)}</Aviso> : null}
        {saude.isLoading && <Carregando texto="Lendo os sinais vitais" />}

        {s && (
          <>
            {/* O veredito, antes de qualquer número. */}
            <section className={`cartao border-2 p-4 ${BORDA[geral]}`}>
              <div className="flex items-baseline justify-between gap-3">
                <p className={`text-2xl font-black ${COR[geral]}`}>
                  {geral === 'ok' ? 'Tudo certo' : geral === 'atencao' ? 'Merece atenção' : 'Precisa de ação'}
                </p>
                <p className="text-xs text-dim">
                  {s.amostra_em ? `medido ${formatarIdade(s.amostra_em, agora)}` : 'sem amostra do agente'}
                </p>
              </div>
              {geral !== 'ok' && (
                <p className="mt-1 text-sm text-mute">
                  {[
                    nBackup !== 'ok' && textoDoBackup(s.backup_em, agora),
                    nDisco !== 'ok' && 'disco enchendo',
                    nMem !== 'ok' && 'memória apertada',
                    nCpu !== 'ok' && 'CPU alta',
                    containers.problemas.length > 0 && `containers: ${containers.problemas.join(', ')}`,
                    nErros !== 'ok' && 'erros HTTP acima do normal',
                  ].filter(Boolean).join(' · ')}
                </p>
              )}
            </section>

            {/* Máquina */}
            <section className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Indicador rotulo="Disco" nivel={nDisco}
                valor={s.disco_total_gb ? `${Math.round((s.disco_usado_gb ?? 0) / s.disco_total_gb * 100)}%` : '—'}
                detalhe={`${qtd(s.disco_usado_gb)} de ${qtd(s.disco_total_gb)} GB`} />
              <Indicador rotulo="Memória" nivel={nMem}
                valor={s.mem_total_mb ? `${Math.round((s.mem_usada_mb ?? 0) / s.mem_total_mb * 100)}%` : '—'}
                detalhe={`${qtd(s.mem_usada_mb)} de ${qtd(s.mem_total_mb)} MB`} />
              <Indicador rotulo="CPU" nivel={nCpu}
                valor={s.cpu_pct === null ? '—' : `${Math.round(s.cpu_pct)}%`}
                detalhe="uso no último minuto" />
              <Indicador rotulo="Containers" nivel={containers.nivel}
                valor={`${containers.rodando}/${containers.total}`}
                detalhe={containers.problemas.length ? containers.problemas.join(', ') : 'todos de pé'} />
            </section>

            {/* Backup: o número que ninguém estava olhando */}
            <section className={`cartao border p-4 ${BORDA[nBackup]}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div>
                  <p className="rotulo">Backup do banco</p>
                  <p className={`text-lg font-bold ${COR[nBackup]}`}>
                    {textoDoBackup(s.backup_em, agora)}
                  </p>
                </div>
                <p className="text-sm text-dim">
                  {s.backup_bytes ? formatarBytes(s.backup_bytes) : ''}
                </p>
              </div>
              {nBackup === 'critico' && (
                <p className="mt-2 text-xs text-erro">
                  O banco guarda estoque, vendas, caixa e clientes. Sem dump recente, uma falha de
                  disco leva tudo.
                </p>
              )}
            </section>

            {/* Tráfego */}
            <section className="cartao p-4">
              <p className="rotulo">Últimas 24 horas</p>
              <div className="mt-1 grid grid-cols-3 gap-3">
                <Numero rotulo="Acessos" valor={qtd(s.acessos_24h)} />
                <Numero rotulo="Erros" valor={qtd(s.erros_24h)} cor={COR[nErros]}
                  detalhe={s.acessos_24h ? `${taxaDeErro(s.acessos_24h, s.erros_24h).toFixed(1)}%` : undefined} />
                <Numero rotulo="Resposta (p95)" valor={s.p95_ms_24h === null ? '—' : `${Math.round(s.p95_ms_24h)} ms`} />
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3 border-t border-edge pt-3">
                <Numero rotulo="Vendas hoje" valor={qtd(s.vendas_hoje)} />
                <Numero rotulo="Faturamento hoje" valor={brl(s.faturamento_hoje)} />
              </div>
            </section>
          </>
        )}

        {/* O placar do mutirão — a razão de esta tela existir HOJE */}
        {metricas.data && <PlacarDoCatalogo m={metricas.data} />}

        {/* Publicação */}
        <section className="cartao space-y-3 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="rotulo">Publicação</p>
              <p className="text-sm text-mute">
                {s?.ultimo_deploy_em
                  ? `Último ${formatarIdade(s.ultimo_deploy_em, agora)} — ${s.ultimo_deploy_status}${s.ultimo_deploy_commit ? ` (${s.ultimo_deploy_commit})` : ''}`
                  : 'Nenhuma publicação registrada.'}
              </p>
            </div>
            <button
              type="button"
              className="botao-primario"
              disabled={publicar.isPending || Boolean(rodando)}
              onClick={() => publicar.mutate()}
            >
              {rodando ? 'Publicando…' : publicar.isPending ? 'Disparando…' : 'Publicar agora'}
            </button>
          </div>

          <p className="text-xs text-dim">
            Traz o código novo do GitHub, constrói site e sistema NA VPS e publica. Não aplica
            mudança de banco — isso continua sendo feito à mão, de propósito.
          </p>

          {publicar.error ? <Aviso tipo="erro">{mensagemDeErro(publicar.error)}</Aviso> : null}

          {rodando && (
            <div className="border border-signal/40 bg-signal/5 p-3">
              <div className="flex items-baseline justify-between text-sm">
                <span className="font-semibold">{rotuloDaEtapa(rodando.etapa) || 'Iniciando'}</span>
                <span className="text-dim">{progressoDoDeploy(rodando.etapa)}%</span>
              </div>
              <div className="mt-2 h-1.5 w-full bg-edge">
                <div className="h-full bg-mute transition-all"
                     style={{ width: `${progressoDoDeploy(rodando.etapa)}%` }} />
              </div>
            </div>
          )}

          {deploys.data && deploys.data.length > 0 && (
            <ul className="divide-y divide-edge">
              {deploys.data.map((d) => <LinhaDeploy key={d.id} deploy={d} agora={agora} />)}
            </ul>
          )}
        </section>

        {/* Erros: para onde se corre quando alguém diz "não abre" */}
        {erros.data && erros.data.length > 0 && (
          <section className="cartao p-4">
            <p className="rotulo">Erros recentes</p>
            <ul className="mt-1 divide-y divide-edge">
              {erros.data.slice(0, 10).map((e, i) => (
                <li key={`${e.em}-${i}`} className="flex items-baseline justify-between gap-3 py-1.5 text-sm">
                  <span className="min-w-0 truncate">
                    <span className="font-mono text-erro">{e.status}</span>{' '}
                    <span className="text-dim">{e.metodo}</span> {e.caminho}
                  </span>
                  <span className="shrink-0 text-xs text-dim">{dataHoraBR(e.em)}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Rotas mais pedidas, com o erro de cada uma ao lado */}
        {rotas.data && rotas.data.length > 0 && (
          <section className="cartao p-4">
            <p className="rotulo">Rotas mais acessadas (7 dias)</p>
            <ul className="mt-1 divide-y divide-edge">
              {rotas.data.slice(0, 10).map((r) => (
                <li key={`${r.host}${r.caminho}`} className="flex items-baseline justify-between gap-3 py-1.5 text-sm">
                  <span className="min-w-0 truncate">{r.caminho}</span>
                  <span className="shrink-0 text-xs">
                    <span className="tabular-nums">{qtd(r.acessos)}</span>
                    {r.erros > 0 && <span className="ml-2 text-erro">{qtd(r.erros)} erro(s)</span>}
                    <span className="ml-2 text-dim">{Math.round(r.media_ms)} ms</span>
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <AoVivo />
      </div>
    </Layout>
  );
}

function Indicador({ rotulo, valor, detalhe, nivel }: {
  rotulo: string; valor: string; detalhe: string; nivel: Nivel;
}) {
  return (
    <div className={`cartao border p-3 ${BORDA[nivel]}`}>
      <p className="rotulo">{rotulo}</p>
      <p className={`text-xl font-black ${COR[nivel]}`}>{valor}</p>
      <p className="text-xs text-dim">{detalhe}</p>
    </div>
  );
}

function Numero({ rotulo, valor, detalhe, cor }: {
  rotulo: string; valor: string; detalhe?: string; cor?: string;
}) {
  return (
    <div>
      <p className="rotulo">{rotulo}</p>
      <p className={`text-lg font-black ${cor ?? ''}`}>{valor}</p>
      {detalhe && <p className="text-xs text-dim">{detalhe}</p>}
    </div>
  );
}

/**
 * O placar do mutirão de cadastro. Cada barra é uma pergunta do dia: quantas
 * peças já dá para VENDER (preço), quantas dá para ACHAR (localização), quantas
 * estão prontas para anunciar (cadastro completo).
 */
function PlacarDoCatalogo({ m }: { m: import('@core/types').MetricasNegocio }) {
  const total = m.produtos_ativos;
  const linhas = [
    { rotulo: 'Com preço de balcão', parte: m.com_preco_balcao },
    { rotulo: 'Com localização', parte: m.com_local },
    { rotulo: 'Cadastro completo', parte: m.cadastro_completo },
    { rotulo: 'Com custo', parte: m.com_custo },
  ];

  return (
    <section className="cartao p-4">
      <div className="flex items-baseline justify-between">
        <p className="rotulo">Catálogo</p>
        <p className="text-2xl font-black">{qtd(total)} <span className="text-sm font-normal text-dim">peças ativas</span></p>
      </div>

      <div className="mt-3 space-y-2">
        {linhas.map((l) => {
          const pct = cobertura(l.parte, total);
          return (
            <div key={l.rotulo}>
              <div className="flex items-baseline justify-between text-xs">
                <span className="text-mute">{l.rotulo}</span>
                <span className="tabular-nums">{qtd(l.parte)} de {qtd(total)} · {pct}%</span>
              </div>
              <div className="mt-0.5 h-1.5 w-full bg-edge">
                <div className="h-full bg-mute" style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>

      {(m.pendente_registro > 0 || m.coringa_pendente > 0) && (
        <p className="mt-3 border-t border-edge pt-3 text-xs text-alerta">
          {qtd(m.pendente_registro)} unidade(s) vendidas sem entrada registrada
          {m.coringa_pendente > 0 && ` · ${qtd(m.coringa_pendente)} em PEÇA DIVERSA (auditar)`}
        </p>
      )}

      <div className="mt-3 grid grid-cols-2 gap-3 border-t border-edge pt-3 sm:grid-cols-4">
        <Numero rotulo="Vendas 30d" valor={qtd(m.vendas_30d)} />
        <Numero rotulo="Faturamento 30d" valor={brl(m.faturamento_30d)} />
        <Numero rotulo="Ticket médio" valor={brl(m.ticket_medio_30d)} />
        <Numero rotulo="Sem estoque" valor={qtd(m.sem_estoque)} />
      </div>
    </section>
  );
}

function LinhaDeploy({ deploy: d, agora }: { deploy: DeployResumo; agora: number }) {
  const [aberto, setAberto] = useState(false);
  const saida = useQuery({
    queryKey: ['sistema', 'saida', d.id],
    queryFn: () => saidaDoDeploy(d.id),
    enabled: aberto,
  });

  const cor = d.status === 'sucesso' ? 'text-ok'
    : d.status === 'rodando' ? 'text-signal-soft' : 'text-erro';

  return (
    <li className="py-2">
      <button type="button" onClick={() => setAberto((v) => !v)}
              className="flex w-full items-baseline justify-between gap-3 text-left text-sm">
        <span className="min-w-0">
          <span className={`font-semibold ${cor}`}>{d.status}</span>
          <span className="text-dim"> · {d.solicitante_nome}</span>
          {d.commit_assunto && <span className="block truncate text-xs text-mute">{d.commit_assunto}</span>}
        </span>
        <span className="shrink-0 text-xs text-dim">
          {formatarIdade(d.iniciado_em, agora)}
          {d.duracao_ms !== null && ` · ${formatarDuracao(d.duracao_ms)}`}
        </span>
      </button>

      {d.erro && <p className="mt-1 text-xs text-erro">{d.erro}</p>}

      {aberto && (
        <div className="mt-2">
          {saida.isLoading && <p className="text-xs text-dim">Carregando a saída…</p>}
          {saida.data && (
            <pre className="max-h-64 overflow-auto border border-edge bg-panel-alt p-2 text-[0.6875rem] leading-snug">
              {saida.data}
            </pre>
          )}
        </div>
      )}
    </li>
  );
}

/**
 * Os sinais vitais AO VIVO, sob demanda. Separado do resto porque é a única
 * coisa aqui que fala direto com o agente por HTTP — se ele estiver fora, o
 * erro aparece SÓ neste bloco e o resto da tela continua servindo.
 */
function AoVivo() {
  const [pedido, setPedido] = useState(false);
  const status = useQuery({
    queryKey: ['sistema', 'aovivo'],
    queryFn: statusDaVps,
    enabled: pedido,
    refetchInterval: pedido ? 10_000 : false,
  });

  return (
    <section className="cartao p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="rotulo">Medir agora</p>
          <p className="text-xs text-dim">
            Pergunta direto ao agente, sem esperar a próxima amostra (que é de minuto em minuto).
          </p>
        </div>
        <button type="button" className="botao-secundario shrink-0"
                onClick={() => setPedido((v) => !v)}>
          {pedido ? 'Parar' : 'Medir'}
        </button>
      </div>

      {status.error ? (
        <div className="mt-3"><Aviso tipo="erro">{mensagemDeErro(status.error)}</Aviso></div>
      ) : null}

      {pedido && status.data && (
        <dl className="mt-3 grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
          <Numero rotulo="CPU" valor={status.data.cpu_pct === null ? '—' : `${Math.round(status.data.cpu_pct)}%`} />
          <Numero rotulo="Carga" valor={status.data.carga_1m === null ? '—' : String(status.data.carga_1m)} />
          <Numero rotulo="Memória" valor={`${qtd(status.data.mem_usada_mb)} MB`} />
          <Numero rotulo="Disco" valor={`${qtd(status.data.disco_usado_gb)} GB`} />
        </dl>
      )}
    </section>
  );
}
